import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { statusBadgeClass } from "../../components/instructor/InstructorShell";
import { STEPS } from "../../components/partner/PartnerShell";

export default function PartnerDashboard() {
  const { partner, refreshPartner } = useAuth();
  const org = partner?.org;
  const completeness = partner?.completeness;
  const sources = partner?.sources || [];

  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(!partner);

  const load = React.useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await refreshPartner();
    } catch (err) {
      setError(err?.message || "We couldn't load your partner profile. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [refreshPartner]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (!partner || !org) {
    if (error) {
      return (
        <div className="max-w-md rounded-2xl border border-red-100 bg-red-50 p-6">
          <p className="font-medium text-red-800">We couldn't load your profile</p>
          <p className="mt-1 text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="mt-4 inline-flex rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {loading ? "Retrying…" : "Try again"}
          </button>
        </div>
      );
    }
    return <div className="text-gray-500">Loading your partner profile…</div>;
  }

  const nextMissing = completeness?.requiredMissing?.[0];
  const nextStep = STEPS.find((s) => s.id === nextMissing?.step) || STEPS[0];
  const scrapeStatus = sources[0]?.scrape_status;
  const showScrapeWait =
    ["Submitted", "UnderReview"].includes(org.status) &&
    ["queued", "in_progress", "pending"].includes(scrapeStatus || "queued");

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-semibold text-gray-900">
          Welcome{org.trade_name ? `, ${org.trade_name}` : ""}
        </h1>
        <p className="mt-1 text-gray-600">
          Partner onboarding for yoga schools and retreat providers in India.
        </p>
      </div>

      {showScrapeWait && (
        <div className="rounded-2xl border border-amber-100 bg-amber-50/80 p-5 text-sm text-amber-950">
          <p className="font-medium">Our team is setting up your programs from your website.</p>
          <p className="mt-1 text-amber-900/80">
            Developers will list offerings from{" "}
            <span className="font-medium break-all">
              {sources[0]?.website_url || org.website || "your site"}
            </span>
            . Approval happens only after listings look correct.
          </p>
        </div>
      )}

      {org.status === "Approved" && (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/80 p-5 text-sm text-emerald-950">
          <p className="font-medium">You are an approved partner.</p>
          <p className="mt-1 text-emerald-900/80">
            Your programs are listed from the catalog our team maintains from your website.
          </p>
        </div>
      )}

      {org.status === "Rejected" && org.rejection_reason && (
        <div className="rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-900">
          <p className="font-medium">Updates needed</p>
          <p className="mt-1 whitespace-pre-wrap">{org.rejection_reason}</p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-gray-100 bg-white p-6 md:col-span-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">Application completeness</p>
              <p className="mt-1 text-4xl font-semibold text-gray-900">
                {completeness?.score ?? 0}%
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Submit requires {completeness?.threshold ?? 85}% and all required items.
              </p>
            </div>
            <span
              className={`rounded-full px-3 py-1 text-sm font-medium ${statusBadgeClass(org.status)}`}
            >
              {org.status}
            </span>
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full bg-emerald-500"
              style={{ width: `${completeness?.score ?? 0}%` }}
            />
          </div>

          {completeness?.missing?.length > 0 && (
            <ul className="mt-6 space-y-2">
              {completeness.missing.map((item) => (
                <li key={item.key} className="flex items-center justify-between text-sm">
                  <span className={item.optional ? "text-gray-500" : "text-gray-800"}>
                    {item.label}
                    {item.optional ? " (optional)" : ""}
                  </span>
                  <Link
                    to={`/partner/onboarding/${item.step}`}
                    className="font-medium text-emerald-700 hover:underline"
                  >
                    Fix
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {["Draft", "Rejected", "Approved"].includes(org.status) && (
              <Link
                to={nextStep.path}
                className="inline-flex rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"
              >
                {profileLabel(org.status, completeness?.score)}
              </Link>
            )}
            <Link
              to="/partner/onboarding/review"
              className="inline-flex rounded-lg border border-emerald-200 px-4 py-2.5 text-sm font-medium text-emerald-800 hover:bg-emerald-50"
            >
              Review
            </Link>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-6 space-y-3 text-sm">
          <p className="font-medium text-gray-900">Website source</p>
          <p className="break-all text-emerald-800">
            {sources[0]?.website_url || org.website || "Not set"}
          </p>
          <p className="text-gray-500">
            Scrape status:{" "}
            <span className="font-medium text-gray-800">{scrapeStatus || "—"}</span>
          </p>
          <p className="text-xs text-gray-500">
            Scraping is done manually by our developers — the app never creates scrapers or Cloud
            Run jobs for you.
          </p>
        </div>
      </div>
    </div>
  );
}

function profileLabel(status, score) {
  if (status === "Approved") return "Edit details";
  if (score) return "Continue onboarding";
  return "Start onboarding";
}
